import logo from "assets/logo_verify.svg";
import { Box, Grid, Typography, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { object, string, TypeOf } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForgotPasswordMutation } from "redux/api/authApi";
import FormInput from "components/FormInput";
import lock from "assets/img-pass.svg";
import home from "assets/img-login-admin.jpg";
import { useIntl } from "react-intl";

const forgotPasswordSchema = object({
  email: string()
    .min(1, "El correo electrónico es requerido")
    .email("Debe indicar un correo electrónico válido"),
});

const LinkItem = styled(Link)`
  text-decoration: none;
  color: ${({ theme }) => theme.palette.secondary.main};
  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.palette.secondary.dark};
  }
`;

export type ForgotPasswordInput = TypeOf<typeof forgotPasswordSchema>;

export const ForgotPasswordPage = () => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();

  const methods = useForm<ForgotPasswordInput>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const [forgotPassword, { isError, isSuccess }] = useForgotPasswordMutation();

  const { handleSubmit, setError } = methods;

  useEffect(() => {
    if (isError) {
      setError("email", { message: "No existe una cuenta asociada a este correo electrónico" });
    }
  }, [isError, setError]);

  const navigateToLogin = () => {
    navigate("/login");
  };

  const onSubmitHandler: SubmitHandler<ForgotPasswordInput> = ({ email }) => {
    forgotPassword({ email });
  };

  if (isSuccess) {
    return (

      <Grid
        container
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          minHeight: "100%",
          height: "100%"
        }}
      >
        <Grid item md={7} sx={{
          height: "100%",
          minHeight: "100%",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundImage: `url(${home})`,
          display: { xs: "none", md: "flex" }
        }}>
        </Grid>
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            maxWidth: "27rem",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "center",
            minHeight: "100%",
            backgroundImage: `url()`,
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            paddingX: {
              xs: "2rem", // Para pantallas pequeñas (móviles)
              md: "4rem", // Para desktops y pantallas más grandes
            }
          }}
        >
          <Box sx={{
            maxWidth: "27rem"
          }}
            display="flex"
            alignItems={"center"}
            justifyContent={"center"}
            flexDirection={"column"}
          >
            <Box
              sx={{
                margin: {
                  xs: "2rem 1.625rem", // Margen superior para teléfonos móviles
                  sm: "2rem", // Margen superior para tablets
                  md: "2rem", // Margen superior para desktop
                  lg: "1.5rem", // Margen superior para desktop
                  xl: "3rem", // Margen superior para desktop
                  // Puedes agregar más breakpoints si es necesario
                },
                height: {
                  xs: "5.25rem", // Altura para teléfonos móviles
                  sm: "80px", // Altura para tablets
                  md: "100px", // Altura para desktop
                  // Puedes agregar más breakpoints si es necesario
                },
              }}
            >
              <img src={logo} style={{ height: '100%', width: 'auto' }} alt="Logo" />
            </Box>

            <Typography
              textAlign="left"
              component="h1"
              color="secondary"
              sx={{
                width: "100%",
                fontWeight: 600,
                fontSize: { xs: "1.5rem", md: "2rem" },
                lineHeight: "1.813rem",
              }}
            >
                {formatMessage({ id: "forgot.password.title" })}              
            </Typography> 
            <Typography
            textAlign="left"
            component="h2"
            sx={{
              width: "100%",
              fontWeight: 400,
              fontSize: { xs: "1rem" },
              marginBottom: "1.2rem",
              marginTop: "1.2rem",
            }}
            >
                {formatMessage({ id: "forgot.password.instructions.sent" })}              
            </Typography>
            <Button color="secondary" variant="contained" sx={{ 
                    width: "100%" }} onClick={navigateToLogin}>
                {formatMessage({ id: "forgot.password.back" })}              
            </Button>

          </Box >
        </Grid >
      </Grid >

    );
  }


  return (
    <Grid
      container
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        minHeight: "100%",
        height: "100%"
      }}
    >
      <Grid item md={7} sx={{
        height: "100%",
        minHeight: "100%",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundImage: `url(${home})`,
        display: { xs: "none", md: "flex" }
      }}>
      </Grid>
      <Grid
        item
        xs={12}
        md={5}
        sx={{
          maxWidth: "27rem",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          minHeight: "100%",
          backgroundImage: `url()`,
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          paddingX: {
            xs: "2rem", // Para pantallas pequeñas (móviles)
            md: "4rem", // Para desktops y pantallas más grandes
          }
        }}
      >
        <Box sx={{
          maxWidth: "27rem"
        }}
          display="flex"
          alignItems={"center"}
          justifyContent={"center"}
          flexDirection={"column"}
        >
          <Box
            sx={{
              margin: {
                xs: "2rem 1.625rem", // Margen superior para teléfonos móviles
                sm: "2rem", // Margen superior para tablets
                md: "2rem", // Margen superior para desktop
                lg: "1.5rem", // Margen superior para desktop
                xl: "3rem", // Margen superior para desktop
                // Puedes agregar más breakpoints si es necesario
              },
              height: {
                xs: "5.25rem", // Altura para teléfonos móviles
                sm: "80px", // Altura para tablets
                md: "100px", // Altura para desktop
                // Puedes agregar más breakpoints si es necesario
              },
            }}
          >
            <img src={logo} style={{ height: '100%', width: 'auto' }} alt="Logo" />
          </Box>

          <Typography
            textAlign="left"
            component="h1"
            color="secondary"
            sx={{
              width: "100%",
              fontWeight: 600,
              fontSize: { xs: "1.5rem", md: "2rem" },
              lineHeight: "1.813rem",
            }}
          >
                {formatMessage({ id: "forgot.password.recover" })}
            
          </Typography>
          <Typography
            textAlign="left"
            component="h2"
            sx={{
              width: "100%",
              fontWeight: 400,
              fontSize: { xs: "1rem" },
              marginBottom: "1.2rem",
              marginTop: "1.2rem",
            }}
          >
                {formatMessage({ id: "forgot.password.forgot" })}
          </Typography>
          <Typography
            textAlign="left"
            component="span"
            sx={{
              width: "100%",
              fontWeight: 400,
              fontSize: { xs: "1rem" },
              marginBottom: "2rem",
            }}
          >
                {formatMessage({ id: "forgot.password.instructions" })}            
          </Typography>

          <FormProvider {...methods}>
            <Box
              component="form"
              onSubmit={handleSubmit(onSubmitHandler)}
              noValidate
              autoComplete="on" // Set autoComplete to "on" to enable browser autocomplete
              maxWidth="27rem"
              width="100%"
            >
              <FormInput
                name="email"
                label="Correo electrónico"
                type="email"
                autoComplete="email" // Set autoComplete to "email" for email field
              />
              <Typography
                sx={{ fontSize: "0.9rem", mb: "3rem", textAlign: "left" }}
              >
                <LinkItem
                  to="/login"
                  style={{
                    fontWeight: "500",
                    fontSize: ".875rem",
                  }}
                >
                {formatMessage({ id: "forgot.password.login" })}                  
                </LinkItem>
              </Typography>
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "1rem",
                }}
              >
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{
                    width: "100%",
                  }}
                  disableElevation
                  type="submit"
                >
                {formatMessage({ id: "forgot.password.reset" })}                  
                </Button>
              </Box>

            </Box>
          </FormProvider>
        </Box>
      </Grid>
    </Grid>
  );
};